import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  Timestamp,
  doc,
  updateDoc,
  writeBatch,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../shared/utils/firebase';
import { useAuth } from '../features/auth/useAuth';
import { useManagedLocations } from '../features/auth/useManagedLocations';
import { whereLocation } from '../features/auth/managedLocations';
import { hasUserDismissed, hasUserRead } from '../shared/utils/notificationStatus';
import { useDeferredActions } from '../shared/utils/useDeferredActions';

export interface Notification {
  id: string;
  type: 'nps_submission' | 'manager_checkin' | 'leave_request_submitted' | 'leave_status_update' | 'success_nomination';
  clientId?: string;
  clientName?: string;
  client?: {
    id: string;
    name: string;
  };
  managerLocation?: string | null;
  message: string;
  timestamp: Timestamp;
  /**
   * Whether the signed-in user has read/acknowledged this notification.
   * Derived from `status.readBy`, so it is independent of other recipients.
   */
  read: boolean;
  metadata?: {
    npsRating?: number;
    checkinType?: string;
    submittedBy?: string;
    submittedByName?: string;
    programmeTags?: string[];
    excerpt?: string;
    staffUid?: string;
    staffName?: string;
    nominationId?: string;
    sessionsCompleted?: number;
    clientLocation?: string;
  };
  recipients?: {
    coachIds?: string[];
    roles?: Array<'ADMIN' | 'MANAGER' | 'USER'>;
    locations?: string[];
  };
}

/** `idle` until there is a signed-in user with a role the feed can be built for. */
export type NotificationFeedStatus = 'idle' | 'loading' | 'ready' | 'error';

/** How long an archived notification can be restored before it is written. */
export const ARCHIVE_UNDO_MS = 5000;

/**
 * Documents read per feed query. Some are never shown (what this person did
 * themselves, and leave requests, which "Waiting on you" reads from the
 * requests directly), so the limit leaves room for them.
 */
const FEED_LIMIT = 75;

interface NotificationContextType {
  /** The signed-in user's feed, minus anything they archived or initiated themselves. */
  notifications: Notification[];
  status: NotificationFeedStatus;
  /** Re-subscribe after the feed query failed. */
  retry: () => void;
  markAsRead: (notificationId: string) => Promise<void>;
  /** Marks the given notifications read, or every unread one when no ids are passed. */
  markAllAsRead: (notificationIds?: string[]) => Promise<void>;
  clearNotification: (notificationId: string) => Promise<void>;
  /** Hides a notification now and clears it after ARCHIVE_UNDO_MS unless undone. */
  archiveNotification: (notificationId: string) => void;
  undoArchive: (notificationId: string) => void;
  /** Notifications archived but not yet written, newest last, for the Undo prompt. */
  pendingArchives: Notification[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Notification documents are shared by every recipient, so read/dismissed
// state is stored per user: `status.readBy` and `status.dismissedBy` hold the
// uids that have acknowledged or cleared the notification. The document-wide
// `read` / `dismissed` flags written at creation are never set to true by the
// app - flipping them would change what every other recipient sees.
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user, userRole, userMetadata } = useAuth();
  const managerLocations = useManagedLocations();
  const [feed, setFeed] = useState<Notification[]>([]);
  const [status, setStatus] = useState<NotificationFeedStatus>('idle');
  const [attempt, setAttempt] = useState(0);

  // Mark a single notification as read for the signed-in user only
  const markAsRead = async (notificationId: string) => {
    const uid = user?.uid;
    if (!uid) return;

    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        'status.readBy': arrayUnion(uid),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark the given (or every) notification the signed-in user has not yet read, in one batch
  const markAllAsRead = async (notificationIds?: string[]) => {
    const uid = user?.uid;
    if (!uid) return;

    const wanted = notificationIds ? new Set(notificationIds) : null;
    const unreadNotifications = feed.filter((n) => !n.read && (!wanted || wanted.has(n.id)));
    if (unreadNotifications.length === 0) return;

    try {
      const batch = writeBatch(db);
      unreadNotifications.forEach((notification) => {
        batch.update(doc(db, 'notifications', notification.id), {
          'status.readBy': arrayUnion(uid),
        });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Clear a notification from the given user's feed only
  const dismissFor = async (notificationId: string, uid: string) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        'status.dismissedBy': arrayUnion(uid),
      });
    } catch (error) {
      console.error('Error clearing notification:', error);
    }
  };

  const clearNotification = async (notificationId: string) => {
    const uid = user?.uid;
    if (!uid) return;
    await dismissFor(notificationId, uid);
  };

  // Rules only let a user add their own uid to `status.dismissedBy`, never
  // remove it, so Undo works by holding the write back rather than reversing it.
  const archives = useDeferredActions<{ uid: string }>(ARCHIVE_UNDO_MS, (notificationId, { uid }) =>
    dismissFor(notificationId, uid)
  );

  const archiveNotification = (notificationId: string) => {
    const uid = user?.uid;
    if (!uid) return;
    archives.schedule(notificationId, { uid });
  };

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  // Set up real-time listeners for notifications
  useEffect(() => {
    if (!user || !userMetadata) {
      setFeed([]);
      setStatus('idle');
      return;
    }

    const uid = user.uid;
    let notificationQuery;

    // `dismissed` is the legacy document-wide flag. The app no longer sets it,
    // but the filter is kept so the existing composite indexes keep serving
    // these queries and notifications retracted before per-user dismissal
    // shipped stay hidden. Per-user dismissal is applied client-side below.
    if (userRole === 'ADMIN') {
      notificationQuery = query(
        collection(db, 'notifications'),
        where('recipients.roles', 'array-contains', 'ADMIN'),
        where('dismissed', '==', false),
        orderBy('timestamp', 'desc'),
        limit(FEED_LIMIT)
      );
    } else if (userRole === 'MANAGER' && managerLocations.length > 0) {
      // Each notification is about one client, so it names one managerLocation;
      // a manager of several sites reads all of theirs with one `in` filter.
      notificationQuery = query(
        collection(db, 'notifications'),
        where('recipients.roles', 'array-contains', 'MANAGER'),
        whereLocation('managerLocation', managerLocations),
        where('dismissed', '==', false),
        orderBy('timestamp', 'desc'),
        limit(FEED_LIMIT)
      );
    } else if (userRole === 'USER' && userMetadata.coach_id) {
      notificationQuery = query(
        collection(db, 'notifications'),
        where('recipients.coachIds', 'array-contains', String(userMetadata.coach_id)),
        where('dismissed', '==', false),
        orderBy('timestamp', 'desc'),
        limit(FEED_LIMIT)
      );
    } else {
      setFeed([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const unsubscribe = onSnapshot(
      notificationQuery,
      (snapshot) => {
        const notificationData: Notification[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();

          // Hidden only for the user who cleared it; other recipients still see it.
          if (hasUserDismissed(data.status, uid)) return;
          // Nobody needs to be told about something they did themselves.
          if (data.initiator?.uid && data.initiator.uid === uid) return;

          notificationData.push({
            id: docSnap.id,
            type: data.type,
            clientId: data.clientId,
            clientName: data.clientName,
            client: data.client || (data.clientId && data.clientName ? { id: data.clientId, name: data.clientName } : undefined),
            managerLocation: data.managerLocation,
            message: data.message,
            timestamp: data.timestamp,
            read: hasUserRead(data.status, uid),
            metadata: data.metadata,
            recipients: data.recipients,
          });
        });

        setFeed(notificationData);
        setStatus('ready');
      },
      (error) => {
        // Keep whatever was already loaded; the panel shows the failure and a retry.
        console.error('Error subscribing to notifications:', error);
        setStatus('error');
      }
    );

    return () => unsubscribe();
  }, [user, userRole, userMetadata, managerLocations, attempt]);

  const notifications = useMemo(
    () => feed.filter((notification) => !(notification.id in archives.pending)),
    [feed, archives.pending]
  );
  const pendingArchives = useMemo(
    () => Object.keys(archives.pending)
      .map((id) => feed.find((notification) => notification.id === id))
      .filter((notification): notification is Notification => Boolean(notification)),
    [feed, archives.pending]
  );

  const value: NotificationContextType = {
    notifications,
    status,
    retry,
    markAsRead,
    markAllAsRead,
    clearNotification,
    archiveNotification,
    undoArchive: archives.cancel,
    pendingArchives,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
